'use client'

import { useState } from 'react'
import { Button } from '@vinted/web-ui'

import { ROOT_URL } from 'constants/routes'
import { ClickableElement } from 'constants/tracking/clickable-elements'
import { Screen } from 'constants/tracking/screens'
import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'
import { useRefUrl } from '@marketplace-web/shared/ref-url'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useSession } from '@marketplace-web/shared/session'
import { clickEvent } from '_libs/common/event-tracker/events'
import { navigateToPage } from '@marketplace-web/shared/browser'
import { acceptTermsAndConditions } from '@marketplace-web/domain/terms-and-conditions-data'

type Props = {
  showAcceptErrorModal: (value: boolean) => void
}

const TermsActions = ({ showAcceptErrorModal }: Props) => {
  const breakpoints = useBreakpoint()
  const refUrl = useRefUrl(ROOT_URL)
  const { track } = useTracking()
  const { user } = useSession()
  const [isAcceptButtonDisabled, setIsAcceptButtonDisabled] = useState(false)

  const translate = useTranslate('terms_and_conditions.actions')
  const isRestrictedByTerms = user?.restricted_by_terms

  const buttonSize = breakpoints.tabletsUp ? undefined : Button.Size.Medium
  const screen = isRestrictedByTerms ? Screen.TermsAndConditionsForced : Screen.TermsAndConditions

  const trackClick = (target: ClickableElement) => {
    track(clickEvent({ target, screen, env: 'js' }))
  }

  const handleAcceptOnTerms = async () => {
    trackClick(ClickableElement.AcceptTerms)
    if (!user?.id) return

    setIsAcceptButtonDisabled(true)

    const response = await acceptTermsAndConditions(user.id)
    if ('errors' in response) {
      setIsAcceptButtonDisabled(false)
      showAcceptErrorModal(true)

      return
    }

    navigateToPage(refUrl)
  }

  return (
    <Button
      text={translate('agree')}
      disabled={isAcceptButtonDisabled}
      styling={Button.Styling.Filled}
      size={buttonSize}
      onClick={handleAcceptOnTerms}
      testId="terms-button-agree"
    />
  )
}

export default TermsActions
